import { type FC, useMemo, useState } from "react";

import { Column, Row, SearchInput, Text } from "@hightouch/htui";

import {
  useMessagesForAudienceBuilderQuery,
  useSubscriptionTopicsForAudienceBuilderQuery,
} from "src/graphql";
import type {
  MessagesForAudienceBuilderQuery,
  SubscriptionTopicsForAudienceBuilderQuery,
} from "src/graphql";
import { type PropertyCondition, StringOperator } from "src/types/visual";
import type { UpdaterEnforceDefined } from "src/utils/immutable-helpers";
import { Filter } from "./filter";
import { getPropertyNameFromColumn } from "./utils";

type Option = {
  value: string;
  label: string;
  description: string;
};

const selectMessages = (data: MessagesForAudienceBuilderQuery): Option[] =>
  data.messages.map((m) => ({
    value: m.id,
    label: m.name ?? m.id,
    description: `ID: ${m.id}`,
  }));

const selectSubscriptionTopics = (
  data: SubscriptionTopicsForAudienceBuilderQuery,
): Option[] =>
  data.cep_subscription_topics.map((t) => ({
    value: t.id,
    label: t.name ?? t.id,
    description: `ID: ${t.id}`,
  }));

const getVariant = (
  columnName: string | undefined,
  messageOptions: Option[],
  topicOptions: Option[],
): { options: Option[]; emptyLabel: string } => {
  switch (columnName) {
    case "message_id":
      return { options: messageOptions, emptyLabel: "No messages found" };
    case "topic_id":
      return {
        options: topicOptions,
        emptyLabel: "No subscription topics found",
      };
    default:
      return { options: [], emptyLabel: "No options found" };
  }
};

type CepModelPropertyFilterProps = {
  condition: PropertyCondition;
  onUpdate: (updater: UpdaterEnforceDefined<PropertyCondition>) => void;
  isDisabled?: boolean;
  operatorError: string | null | undefined;
  valueError: string | null | undefined;
};

export const CepModelPropertyFilter: FC<
  Readonly<CepModelPropertyFilterProps>
> = ({ condition, onUpdate, isDisabled, operatorError, valueError }) => {
  const [search, setSearch] = useState("");

  const columnName = getPropertyNameFromColumn(
    condition.property,
  )?.toLowerCase();

  const { data: messageOptions = [] } = useMessagesForAudienceBuilderQuery(
    {},
    { select: selectMessages, enabled: columnName === "message_id" },
  );
  const { data: topicOptions = [] } =
    useSubscriptionTopicsForAudienceBuilderQuery(
      {},
      { select: selectSubscriptionTopics, enabled: columnName === "topic_id" },
    );

  const { options, emptyLabel } = getVariant(
    columnName,
    messageOptions,
    topicOptions,
  );

  const selectedOption = useMemo(
    () =>
      condition.value != null
        ? options.find((o) => o.value === String(condition.value))
        : undefined,
    [options, condition.value],
  );

  const filteredOptions = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(term) || o.value.includes(term),
    );
  }, [options, search]);

  return (
    <Filter
      fullWidth
      isEmpty={condition.value == null}
      isDisabled={isDisabled}
      isInvalid={Boolean(valueError || operatorError)}
      error={valueError || operatorError}
      onClose={() => setSearch("")}
      content={({ onClose }) => (
        <Column minWidth="400px" gap={2}>
          <Column
            p={2}
            borderBottomWidth="hairline"
            borderBottomStyle="solid"
            borderColor="base.divider"
          >
            <SearchInput
              placeholder="Search by name or ID…"
              value={search}
              width="auto"
              onChange={(e) => setSearch(e.target.value)}
            />
          </Column>
          <Column overflowY="auto" maxHeight="240px" gap={2}>
            {filteredOptions.map((option) => {
              const isSelected = option.value === selectedOption?.value;
              return (
                <Column
                  as="button"
                  key={option.value}
                  borderRadius="xl"
                  border={isSelected ? "1px solid" : undefined}
                  borderColor="primary.border"
                  px={2}
                  py={2.5}
                  gap={0.5}
                  width="100%"
                  alignItems="flex-start"
                  bg={isSelected ? "primary.background" : "transparent"}
                  _hover={{
                    bg: isSelected ? "primary.background" : "base.background",
                  }}
                  onClick={() => {
                    onUpdate((c) => {
                      c.operator = StringOperator.Equals;
                      c.value = option.value;
                    });
                    onClose();
                  }}
                >
                  <Text
                    isTruncated
                    color={isSelected ? "primary.hover" : undefined}
                    fontWeight={isSelected ? "medium" : "normal"}
                  >
                    {option.label}
                  </Text>
                  <Text
                    color={isSelected ? "primary.hover" : "text.secondary"}
                    size="sm"
                  >
                    {option.description}
                  </Text>
                </Column>
              );
            })}
            {filteredOptions.length === 0 && (
              <Row px={3} py={2}>
                <Text color="text.secondary">{emptyLabel}</Text>
              </Row>
            )}
          </Column>
        </Column>
      )}
    >
      <Text>{selectedOption ? `is ${selectedOption.label}` : "is…"}</Text>
    </Filter>
  );
};
